import { LinkNode } from '@lexical/link';
import { MarkNode } from '@lexical/mark';
import { getStyleObjectFromCSS } from '@lexical/selection';
import { TextNode } from 'lexical';
import * as BrandDefinition from 'modules/BrandDefinition';
import { $isProjectMetadataTokenNode } from 'modules/Lexical/nodes/ProjectMetadataTokenNode';
import { ReferenceCitationNode } from 'modules/Lexical/nodes/ReferenceCitationNode';
import { ReferenceCitationSeparatorNode } from 'modules/Lexical/nodes/ReferenceCitationSeparatorNode';
import { CSSProperties } from 'react';
import * as Constants from 'const';
import * as Models from 'models';
import { DefaultFontFamily } from 'services/ArtboardConverter/constants';
import { Schemas } from 'services/ArtboardConverter/models';
import { getDesktopBrandStyles } from 'utils/brandStyles';
import { getIntegerFromStyle } from 'utils/getIntegerFromStyle';
import { toPx } from 'utils/toPx';
import { FontPluginStyle, getBrandColorRefFromStyleObject, getBrandFontRefFromStyleObject } from '../../Plugins/FontPlugin/style';

function updateFontFamily(
  fontFamily: string,
  fontWeight: string = Constants.CharStyleValue.REGULAR,
  fontStyle: string = Constants.CharStyleValue.NORMAL,
): string {
  const fontFamilies = fontFamily.split(',');
  const fallBackFonts = fontFamilies.slice(1);
  const unwrappedMainFontFamily = fontFamilies[0].trim().replace(/'/g, '');

  let updatedUnwrappedFontFamily;
  const isUniqueFontFamilyNeeded = fontWeight !== Constants.CharStyleValue.REGULAR
    && fontWeight !== Constants.CharStyleValue.BOLD;
  if (isUniqueFontFamilyNeeded) {
    updatedUnwrappedFontFamily = unwrappedMainFontFamily.includes(' ')
      ? `'${unwrappedMainFontFamily}-${fontStyle}-${fontWeight}'`
      : `${unwrappedMainFontFamily}-${fontStyle}-${fontWeight}`;
  } else {
    updatedUnwrappedFontFamily = unwrappedMainFontFamily.includes(' ')
      ? `'${unwrappedMainFontFamily}'`
      : `${unwrappedMainFontFamily}`;
  }

  return [updatedUnwrappedFontFamily, ...fallBackFonts].join(', ').replace(/\s+/g, ' ');
}

// textTransform property will be overrided on the upper level if needed
function getTextTransformNoneValue(
  node: TextNode,
  brandStyle: Models.BrandStyleMap | undefined,
): CSSProperties['textTransform'] | undefined {
  const typesWithDisabledTextTransform = [
    MarkNode.getType(), // for abbreviations
    LinkNode.getType(),
    ReferenceCitationNode.getType(),
    ReferenceCitationSeparatorNode.getType(),
  ] as string[];

  // we need to check the parent node type because the node we receive in the method
  // is just the text inside the node of the type we're interested in
  const parentNode = node.getParent();

  if (parentNode && typesWithDisabledTextTransform.includes(parentNode.getType()) && brandStyle) {
    const breakpointStyles = getDesktopBrandStyles(brandStyle);
    const textTransform = breakpointStyles?.get(Models.BrandStyleProp.TEXT_TRANSFORM);
    if (textTransform && textTransform !== 'none') {
      return 'none';
    }
  }

  return undefined;
}

export type CreatePartContext = {
  fontSize: number;
  fontFamily?: string;
  lineHeight: number;
  bulletColor: string;
  color: string;
  colors: Models.BrandColorsList;
  fonts: Models.BrandFontsList;
  brandStyle: Models.BrandStyleMap | undefined;
};

export function createPart(
  node: TextNode,
  context: CreatePartContext,
  isLayoutPreview = false,
): Schemas.Part {
  const {
    lineHeight,
    bulletColor,
    colors,
    fonts,
    brandStyle,
  } = context;
  const style = getStyleObjectFromCSS(node.getStyle()) as Record<string, string | undefined>;

  // ensures that actual brandStyle colors are taken into account
  const colorRef = getBrandColorRefFromStyleObject(style);
  const color = colorRef ? BrandDefinition.getBrandColorValueHEX(colors, colorRef) : undefined;

  // ensures that actual brandStyle fonts are taken into account
  const fontRef = getBrandFontRefFromStyleObject(style);
  const fontFamily = BrandDefinition.getBrandFontValueFontFamily(fonts, fontRef, false, context.fontFamily);
  const fontWeight = BrandDefinition.getBrandFontValueFontWeight(fonts, fontRef, false) ?? style[FontPluginStyle.FONT_WEIGHT];
  const fontStyle = BrandDefinition.getBrandFontValueFontStyle(fonts, fontRef, false) ?? style[FontPluginStyle.FONT_STYLE];

  const fontSize = style[FontPluginStyle.FONT_SIZE] ?? toPx(context.fontSize);
  let fontSizeNumber = getIntegerFromStyle(fontSize);
  if (node.hasFormat('subscript') || node.hasFormat('superscript')) {
    fontSizeNumber *= Constants.SCRIPT_BASIC_FONT_REDUCTION;
  }

  // for text editor we are using font styles to mark italic and bold
  // for references editor - we are using node format
  const isBold = fontWeight === Constants.CharStyleValue.BOLD || node.hasFormat('bold');
  const isItalic = fontStyle === Constants.CharStyleValue.ITALIC || node.hasFormat('italic');

  return {
    bold: isBold,
    italic: isItalic,
    sub: node.hasFormat('subscript'),
    sup: node.hasFormat('superscript'),
    underline: node.hasFormat('underline'),
    fontFamily: fontFamily ? updateFontFamily(fontFamily, fontWeight, fontStyle) : DefaultFontFamily,
    fontSize: toPx(fontSizeNumber, 2),
    color: color ?? context.color,
    text: $isProjectMetadataTokenNode(node) && isLayoutPreview ? node.getName() : node.getTextContent(),
    textTransform: getTextTransformNoneValue(node, brandStyle),
    lineHeight: lineHeight.toString(),
    lineHeightPx: toPx(fontSizeNumber * lineHeight, 2),
    bulletColor,
  };
}
